// Allowlist HTML sanitiser for admin-authored rich text (Mitteilungen, FIX 5;
// erweitert Aufgabe 137 — §6.50: "full HTML" mit genau einer Grenze).
// Parses via DOMParser (never innerHTML on untrusted input) and rebuilds a
// clean tree element-by-element: an allowed tag is recreated fresh (so it can
// NEVER carry over an attacker's attributes — only a small, per-tag set of
// attributes is re-set, and only after validating each value), a disallowed
// tag is unwrapped (its text/children survive, the tag itself doesn't), and
// script/style/iframe are dropped together with their entire subtree — their
// "content" is markup/code, not text a reader should ever see.
//
// §6.50.1: everything that can make a Mitteilung LOOK like it should is
// allowed (headings, tables, quotes, images, `style` for colour/size/weight/
// spacing); everything that could make it DO something is not — `<script>`,
// `<iframe>`, `<object>`/`<embed>`, `<form>`/`<input>`, every `on*` handler,
// `javascript:` URLs, and CSS injection vectors (`url(`, `expression(`,
// `behavior:`, `@import`). Object/embed/form/input are simply absent from
// ALLOWED_TAGS, so they're unwrapped like any other unknown tag — the tag
// itself (and every attribute on it) never survives into the output.

const ALLOWED_TAGS = new Set([
  'p', 'br', 'b', 'strong', 'i', 'em', 'u', 's', 'sub', 'sup', 'small',
  'ul', 'ol', 'li', 'a',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'span', 'div', 'blockquote', 'pre', 'code', 'hr',
  'table', 'thead', 'tbody', 'tr', 'th', 'td',
  'img', 'figure', 'figcaption',
])

// Removed wholesale, subtree and all — never unwrapped into visible text.
const DROP_WITH_CONTENT = new Set(['script', 'style', 'iframe'])

const ALLOWED_LINK_SCHEME = /^(https?:|mailto:)/i
// §6.50.1: kein http: (gemischter Inhalt/Tracking) und kein data: ausser
// eingebetteten Bildern — data:text/html waere ein script-Vektor.
const ALLOWED_IMG_SRC = /^(https:|data:image\/)/i
const ALLOWED_ALIGN = new Set(['left', 'center', 'right', 'justify'])
const POSITIVE_INT = /^\d+$/
const LENGTH_ATTR = /^\d+%?$/

// §6.50.1: nur diese CSS-Eigenschaften — Farbe, Groesse, Ausrichtung,
// Gewicht, Abstaende. Alles andere (background-image, position, transform,
// content, …) faellt durch, egal wie harmlos es klaenge.
const ALLOWED_STYLE_PROPS = new Set([
  'color', 'background-color', 'font-size', 'font-weight', 'font-style',
  'text-align', 'text-decoration', 'margin', 'padding', 'border',
  'border-radius', 'width', 'max-width', 'line-height', 'letter-spacing',
  'font-family',
])

// Bekannte CSS-Injektionsvektoren; jede Deklaration mit einem Treffer wird
// verworfen, nicht nur entschaerft.
const FORBIDDEN_STYLE_VALUE = /url\(|expression\(|javascript:|behavior:|@import|</i

const MAX_FONT_SIZE_PX = 72

function clampFontSize(value: string): string {
  const m = value.match(/^(\d+(?:\.\d+)?)px$/i)
  if (!m) return value
  return Number(m[1]) > MAX_FONT_SIZE_PX ? `${MAX_FONT_SIZE_PX}px` : value
}

/**
 * Filtert den Wert eines `style`-Attributs auf eine feste Eigenschaftsliste
 * herunter — nie ein Durchreichen. Arbeitet rein auf Strings (kein DOM/CSSOM),
 * damit sie ohne Browser testbar ist. Eine Eigenschaft ausserhalb der
 * Erlaubnisliste, oder ein Wert mit einem bekannten CSS-Injektionsvektor
 * (`url(`, `expression(`, `javascript:`, `behavior:`, `@import`, `<`), wird
 * vollstaendig verworfen statt nur teilweise entschaerft. `font-size` wird
 * auf 72px gedeckelt — ein `font-size: 500px` in einer Mitteilung wuerde
 * sonst jede Ansicht sprengen (Owner 11.09.2026).
 */
export function sanitizeStyle(styleText: string): string {
  const kept: string[] = []
  for (const decl of String(styleText ?? '').split(';')) {
    const sep = decl.indexOf(':')
    if (sep === -1) continue
    const prop = decl.slice(0, sep).trim().toLowerCase()
    let value = decl.slice(sep + 1).trim()
    if (!prop || !value) continue
    if (!ALLOWED_STYLE_PROPS.has(prop)) continue
    if (FORBIDDEN_STYLE_VALUE.test(value)) continue
    if (prop === 'font-size') value = clampFontSize(value)
    kept.push(`${prop}: ${value}`)
  }
  return kept.join('; ')
}

function appendChildrenSanitized(source: Element | DocumentFragment, target: Node): void {
  for (const child of Array.from(source.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) {
      target.appendChild(child.cloneNode(true))
      continue
    }
    if (child.nodeType !== Node.ELEMENT_NODE) continue // comments, etc. — drop

    const el = child as Element
    const tag = el.tagName.toLowerCase()

    if (DROP_WITH_CONTENT.has(tag)) continue // drop entirely, don't recurse

    // §6.50.7 — <font color="red" size="5"> ist das, was viele Editoren (und
    // Outlook-Copy-Paste) erzeugen. Es stand nicht auf der Liste, wurde also
    // entfernt, und die Farbe verschwand still: „I am red" kam schwarz an.
    // Statt das tote Tag zu erlauben, wird es in ein <span> mit gefiltertem
    // style übersetzt — dieselbe Prüfung wie bei jedem anderen style.
    if (tag === 'font') {
      const span = document.createElement('span')
      const decls: string[] = []
      const color = (el.getAttribute('color') ?? '').trim()
      if (color) decls.push(`color: ${color}`)
      const face = (el.getAttribute('face') ?? '').trim()
      if (face) decls.push(`font-family: ${face}`)
      // HTML-size 1..7 -> px, grob nach der alten Browser-Tabelle.
      const size = (el.getAttribute('size') ?? '').trim()
      const FONT_SIZE_PX: Record<string, string> = { '1': '10px', '2': '13px', '3': '16px', '4': '18px', '5': '24px', '6': '32px', '7': '48px' }
      if (FONT_SIZE_PX[size]) decls.push(`font-size: ${FONT_SIZE_PX[size]}`)
      const style = sanitizeStyle([...decls, el.getAttribute('style') ?? ''].join('; '))
      if (style) span.setAttribute('style', style)
      appendChildrenSanitized(el, span)
      target.appendChild(span)
      continue
    }

    if (!ALLOWED_TAGS.has(tag)) {
      // Strip the tag, keep its content (recursively sanitized) in its place.
      // Covers script-adjacent-but-not-script tags too: object/embed/form/
      // input never appear in ALLOWED_TAGS, so they (and every attribute on
      // them, on* included) are gone — only their inner text, if any, stays.
      appendChildrenSanitized(el, target)
      continue
    }

    const clean = document.createElement(tag)

    // §6.50.1: `style` ist auf jedem erlaubten Tag erlaubt (der Owner schrieb
    // `<p style="color:blue">`), aber gefiltert — nie durchgereicht.
    const style = sanitizeStyle(el.getAttribute('style') ?? '')
    if (style) clean.setAttribute('style', style)

    const align = (el.getAttribute('align') ?? '').trim().toLowerCase()
    if (ALLOWED_ALIGN.has(align)) clean.setAttribute('align', align)

    if (tag === 'th' || tag === 'td') {
      const colspan = el.getAttribute('colspan') ?? ''
      if (POSITIVE_INT.test(colspan)) clean.setAttribute('colspan', colspan)
      const rowspan = el.getAttribute('rowspan') ?? ''
      if (POSITIVE_INT.test(rowspan)) clean.setAttribute('rowspan', rowspan)
    }

    if (tag === 'img') {
      const src = (el.getAttribute('src') ?? '').trim()
      if (ALLOWED_IMG_SRC.test(src)) clean.setAttribute('src', src)
      // No/invalid src → keep the tag but not a broken/unsafe one; alt still
      // carries meaning (e.g. screen readers) even without an image.
      const alt = el.getAttribute('alt')
      if (alt != null) clean.setAttribute('alt', alt)
      const width = el.getAttribute('width') ?? ''
      if (LENGTH_ATTR.test(width)) clean.setAttribute('width', width)
      const height = el.getAttribute('height') ?? ''
      if (LENGTH_ATTR.test(height)) clean.setAttribute('height', height)
    }

    if (tag === 'a') {
      const href = (el.getAttribute('href') ?? '').trim()
      if (ALLOWED_LINK_SCHEME.test(href)) {
        clean.setAttribute('href', href)
        clean.setAttribute('rel', 'noopener noreferrer')
        clean.setAttribute('target', '_blank')
      }
      // No/invalid href → keep the tag (and its text) but not a dead/unsafe link.
    }
    appendChildrenSanitized(el, clean)
    target.appendChild(clean)
  }
}

/**
 * Sanitizes `html` down to the allowlisted subset above. Every other tag is
 * unwrapped (text kept); script/style/iframe are removed with their content;
 * every attribute is dropped except the small, per-tag validated set above
 * (`style` filtered through {@link sanitizeStyle}, `align`, `colspan`/
 * `rowspan` on table cells, `src`/`alt`/`width`/`height` on `img`, `href` on
 * `a`). Safe to feed straight into `dangerouslySetInnerHTML`.
 */
export function sanitizeHtml(html: string): string {
  const parsed = new DOMParser().parseFromString(html, 'text/html')
  const out = document.createElement('div')
  appendChildrenSanitized(parsed.body, out)
  return out.innerHTML
}

/**
 * §6.50.2/C: gemeinsame Klassenkette fuer die HTML-Wiedergabe einer
 * Mitteilung — von der Vorschau im Admin-Editor UND der Mitarbeiterseite
 * benutzt, damit exakt dasselbe herauskommt, was der Sanitizer durchlaesst.
 * Nur die Darstellung des Inhalts (Kind-Selektoren); Schriftgroesse, Farbe
 * und Rahmen der jeweiligen Aussenbox bleiben Sache der aufrufenden Stelle.
 * `overflow-x-auto` sitzt auf dem Container selbst, nicht in einem
 * Kind-Selektor — eine breite Tabelle scrollt innerhalb der Box statt die
 * Seite zu sprengen.
 */
export const MITTEILUNG_HTML_CLASSES = [
  'overflow-x-auto',
  '[&_p]:mb-2 [&_p]:last:mb-0',
  '[&_ul]:list-disc [&_ol]:list-decimal [&_ul]:ps-5 [&_ol]:ps-5 [&_li]:mb-1',
  '[&_a]:underline',
  '[&_h1]:font-bold [&_h1]:text-lg [&_h1]:mb-2 [&_h1]:text-on-surface',
  '[&_h2]:font-bold [&_h2]:text-base [&_h2]:mb-2 [&_h2]:text-on-surface',
  '[&_h3]:font-bold [&_h3]:text-on-surface',
  '[&_h4]:font-bold [&_h4]:text-on-surface',
  '[&_h5]:font-bold [&_h5]:text-on-surface',
  '[&_h6]:font-bold [&_h6]:text-on-surface',
  '[&_blockquote]:border-s-4 [&_blockquote]:border-outline-variant [&_blockquote]:ps-3 [&_blockquote]:italic [&_blockquote]:my-2',
  '[&_pre]:bg-surface-container-low [&_pre]:rounded-lg [&_pre]:p-2 [&_pre]:overflow-x-auto [&_pre]:my-2',
  '[&_code]:font-mono [&_code]:text-[0.9em] [&_code]:bg-surface-container-low [&_code]:rounded [&_code]:px-1',
  '[&_hr]:border-outline-variant [&_hr]:my-3',
  '[&_table]:border-collapse [&_table]:my-2',
  '[&_th]:border [&_th]:border-outline-variant [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_th]:bg-surface-container-low',
  '[&_td]:border [&_td]:border-outline-variant [&_td]:px-2 [&_td]:py-1',
  '[&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-lg [&_img]:my-2',
  '[&_small]:text-[0.85em]',
  '[&_sub]:align-sub [&_sub]:text-[0.75em]',
  '[&_sup]:align-super [&_sup]:text-[0.75em]',
  '[&_figure]:my-2',
  '[&_figcaption]:text-[0.85em] [&_figcaption]:text-on-surface-variant',
].join(' ')

/**
 * Nur der lesbare Text, ohne jede Auszeichnung — für einzeilige Vorschauen.
 *
 * Owner 11.09.2026 (Bildschirmfoto der Übersicht): dort stand wörtlich
 * `<!DOCTYPE html> <p>I am normal</p> <p>I am red</p> …` in der Zeile unter
 * dem Titel. Die Übersicht gab `m.text` roh als Text aus, während die
 * Mitteilungsseite dieselbe Nachricht längst als HTML rendert. Wer eine
 * Mitteilung als HTML schreibt, darf ihre Quelltextzeichen nirgends zu sehen
 * bekommen.
 *
 * Für eine Vorschau ist gerendertes HTML der falsche Weg (Absätze und Listen
 * sprengen eine einzelne Zeile), deshalb hier: Auszeichnung entfernen,
 * Zeilenumbrüche zu Leerzeichen, mehrfache Leerzeichen zusammenziehen.
 * Ein DOCTYPE, `<html>`/`<head>`/`<body>` und Kommentare verschwinden dabei
 * von selbst — der Parser kennt sie als Struktur, nicht als Text.
 */
export function plainTextExcerpt(html: string): string {
  const parsed = new DOMParser().parseFromString(String(html ?? ''), 'text/html')
  for (const el of Array.from(parsed.body.querySelectorAll('script, style, iframe'))) el.remove()
  return (parsed.body.textContent ?? '').replace(/\s+/g, ' ').trim()
}
